// ─────────────────────────────────────────────────────────────────────────────
// HrDocumentViewerPage: view, edit and issue a joiner's company letters (offer letter, appointment
// letter, enrolment form, induction, NDA, code of conduct).
//
// The left panel lists the joiner's letters. The right panel shows the selected letter on the
// company letterhead. The letter is fetched with the login token and shown in an iframe from
// its HTML, so the page needs no public link. HR edits it in place, saves the draft, and finalises
// it: that makes the PDF and marks the letter issued. Editing an issued letter makes it a draft
// again until it is finalised again.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import { ROUTES } from '@/routes/paths';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import {
  ArrowLeft, FileText, CheckCircle, Clock, Download, Bold, Italic, Underline,
  Undo2, Redo2, AlignLeft, AlignCenter, AlignRight, Loader2, Save, BadgeCheck, Printer,
} from 'lucide-react';
import toast from 'react-hot-toast';

interface CompanyDoc {
  code: string;
  label: string;
  status: string;
  hasFile?: boolean;
  generatedAt?: string;
}

interface CandidateForDocs {
  _id: string;
  candidateId: string;
  employeeId?: string;
  firstName: string;
  lastName: string;
  companyDocuments: CompanyDoc[];
}

const BASE = '/api/hr-onboarding-candidates/candidates';

function useCandidateForDocs(id?: string) {
  return useQuery({
    queryKey: ['onboarding-candidates', 'detail', id],
    queryFn: async () => (await api.get(`${BASE}/${id}`)).data as CandidateForDocs,
    enabled: !!id,
  });
}

function useOnboardingAccess() {
  return useQuery({
    queryKey: ['onboarding-candidates', 'access'],
    queryFn: async () => (await api.get<{ canManage: boolean }>('/api/hr-onboarding-candidates/access')).data,
    staleTime: 60_000,
  });
}

/** The letter as a letterhead page. Fetched with the token, so it works without a public link. */
function useLetterHtml(candidateId?: string, docCode?: string, version?: string) {
  return useQuery({
    queryKey: ['onboarding-candidates', 'letter', candidateId, docCode, version],
    queryFn: async () => {
      const { data } = await api.get<string>(`${BASE}/${candidateId}/company-documents/${docCode}/html`, { responseType: 'text', transformResponse: (r) => r });
      return data;
    },
    enabled: !!candidateId && !!docCode,
    retry: false,
    staleTime: 0,
  });
}

function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${name.replace(/[^a-z0-9-_ ]/gi, '').trim() || 'document'}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function HrDocumentViewerPage() {
  const { candidateId, docCode } = useParams<{ candidateId: string; docCode: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: candidate, isLoading } = useCandidateForDocs(candidateId);
  const { data: access } = useOnboardingAccess();
  const canManage = access?.canManage === true;
  const activeDoc = candidate?.companyDocuments?.find((d) => d.code === docCode);
  const issued = activeDoc?.status === 'issued' || activeDoc?.status === 'acknowledged';

  // Reload the letter whenever its saved state changes.
  const version = `${activeDoc?.status ?? ''}-${activeDoc?.generatedAt ?? ''}`;
  const { data: html, isLoading: letterLoading, error: letterError } = useLetterHtml(candidateId, docCode, version);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [ready, setReady] = useState(false);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setReady(false); setDirty(false); }, [docCode, html]);

  const content = () => iframeRef.current?.contentDocument?.querySelector('.lh-content') as HTMLElement | null;

  function handleIframeLoad() {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    setReady(true);
    doc.querySelector('.lh-content')?.addEventListener('input', () => setDirty(true));
  }

  function exec(command: string, value?: string) {
    const win = iframeRef.current?.contentWindow;
    const doc = iframeRef.current?.contentDocument;
    if (!win || !doc) return;
    win.focus();
    doc.execCommand(command, false, value);
    setDirty(true);
  }

  const refresh = () => qc.invalidateQueries({ queryKey: ['onboarding-candidates'] });
  const save = useMutation({
    mutationFn: async () => {
      const el = content();
      if (!el) throw new Error('The letter is not ready yet.');
      await api.put(`${BASE}/${candidateId}/company-documents/${docCode}`, { bodyHtml: el.innerHTML });
    },
    onSuccess: () => { setDirty(false); toast.success('Draft saved.'); refresh(); },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });
  const finalize = useMutation({
    mutationFn: async () => {
      // Whatever is on screen is what gets issued.
      const el = content();
      if (el && (dirty || activeDoc?.status === 'not_generated')) await api.put(`${BASE}/${candidateId}/company-documents/${docCode}`, { bodyHtml: el.innerHTML });
      await api.post(`${BASE}/${candidateId}/company-documents/${docCode}/finalize`);
    },
    onSuccess: () => { setDirty(false); toast.success('Letter finalised and issued.'); refresh(); },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });
  const download = useMutation({
    mutationFn: async () => {
      const name = activeDoc?.label || 'document';
      // An issued letter has a stored PDF; otherwise make one from what is on screen.
      if (issued && !dirty && activeDoc?.hasFile) {
        const { data } = await api.get<Blob>(`${BASE}/${candidateId}/company-documents/${docCode}/file`, { responseType: 'blob' });
        saveBlob(data, name);
        return;
      }
      const el = content();
      if (!el) throw new Error('The letter is not ready yet.');
      const { data } = await api.post<Blob>('/api/letterhead/pdf', { title: name, docType: docCode, bodyHtml: el.innerHTML }, { responseType: 'blob' });
      saveBlob(data, name);
    },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });

  const toolBtn = (label: string, Icon: typeof Bold, action: () => void) => (
    <button
      key={label}
      type="button"
      title={label}
      aria-label={label}
      disabled={!canManage}
      onMouseDown={(e) => { e.preventDefault(); action(); }}
      className="inline-flex h-8 w-8 items-center justify-center rounded-md text-on-surface-variant hover:bg-surface-variant hover:text-on-surface active:bg-surface-dim disabled:opacity-40"
    >
      <Icon className="h-4 w-4" />
    </button>
  );

  const busy = save.isPending || finalize.isPending;
  const errorText = letterError ? getApiErrorMessage(letterError) : '';

  return (
    <div className="flex h-full overflow-hidden">
      {/* Left: the joiner's letters */}
      <div className="flex w-72 shrink-0 flex-col overflow-hidden border-r border-outline bg-surface">
        <div className="border-b border-outline p-4">
          <button
            type="button"
            onClick={() => navigate(ROUTES.hrOnboarding)}
            className="inline-flex items-center gap-1.5 text-body-sm text-on-surface-variant hover:text-on-surface"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Onboarding
          </button>
          {candidate && (
            <div className="mt-3">
              <p className="font-semibold text-on-surface">{candidate.firstName} {candidate.lastName}</p>
              <p className="text-body-sm text-on-surface-variant font-mono">{candidate.candidateId}{candidate.employeeId ? ` · ${candidate.employeeId}` : ''}</p>
            </div>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {isLoading && <div className="p-4"><Spinner /></div>}
          {candidate?.companyDocuments?.map((doc) => {
            const isActive = doc.code === docCode;
            const done = doc.status === 'issued' || doc.status === 'acknowledged';
            return (
              <button
                key={doc.code}
                type="button"
                onClick={() => navigate(`/app/hr/onboarding/${candidateId}/documents/${doc.code}`)}
                className={`mb-1 flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-body-sm transition-colors ${
                  isActive ? 'bg-primary-container text-on-primary-container' : 'text-on-surface hover:bg-surface-variant'
                }`}
              >
                {done ? <CheckCircle className="h-3.5 w-3.5 shrink-0 text-success" /> : <Clock className="h-3.5 w-3.5 shrink-0 text-on-surface-variant" />}
                <span className="truncate">{doc.label}</span>
                <span className="ml-auto text-[10px] capitalize text-on-surface-variant">{doc.status.replace(/_/g, ' ')}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Right: the letter */}
      <div className="flex flex-1 flex-col overflow-hidden bg-surface-container-low">
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center"><Spinner /></div>
        ) : !activeDoc ? (
          <div className="flex flex-1 items-center justify-center">
            <EmptyState icon={<FileText className="h-6 w-6" />} title="Select a document" description="Choose a document from the list on the left." />
          </div>
        ) : errorText ? (
          <div className="flex flex-1 items-center justify-center">
            <EmptyState icon={<FileText className="h-6 w-6" />} title={activeDoc.label} description={errorText} />
          </div>
        ) : (
          <>
            <div className="flex shrink-0 items-center gap-1 overflow-x-auto border-b border-outline bg-surface px-3 py-1.5">
              <span className="mr-2 truncate text-body-sm font-medium text-on-surface">{activeDoc.label}</span>
              <span className="mr-1 rounded bg-surface-variant px-1.5 py-0.5 text-[10px] capitalize text-on-surface-variant">{activeDoc.status.replace(/_/g, ' ')}{dirty ? ' · unsaved changes' : ''}</span>
              <Divider />
              {toolBtn('Undo', Undo2, () => exec('undo'))}
              {toolBtn('Redo', Redo2, () => exec('redo'))}
              <Divider />
              {toolBtn('Bold', Bold, () => exec('bold'))}
              {toolBtn('Italic', Italic, () => exec('italic'))}
              {toolBtn('Underline', Underline, () => exec('underline'))}
              <Divider />
              {toolBtn('Align left', AlignLeft, () => exec('justifyLeft'))}
              {toolBtn('Align center', AlignCenter, () => exec('justifyCenter'))}
              {toolBtn('Align right', AlignRight, () => exec('justifyRight'))}
              <div className="ml-auto flex items-center gap-2">
                {canManage && (
                  <>
                    <button
                      type="button"
                      onClick={() => save.mutate()}
                      disabled={!ready || busy || !dirty}
                      className="inline-flex items-center gap-1.5 rounded-md border border-outline px-3 py-1.5 text-body-sm font-medium text-on-surface hover:bg-surface-variant disabled:opacity-50"
                    >
                      {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save draft
                    </button>
                    <button
                      type="button"
                      onClick={() => finalize.mutate()}
                      disabled={!ready || busy}
                      title="Make the PDF and mark this letter issued"
                      className="inline-flex items-center gap-1.5 rounded-md border border-outline px-3 py-1.5 text-body-sm font-medium text-on-surface hover:bg-surface-variant disabled:opacity-50"
                    >
                      {finalize.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BadgeCheck className="h-3.5 w-3.5" />} {issued && !dirty ? 'Re-issue' : 'Finalise & issue'}
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => {
                    const win = iframeRef.current?.contentWindow;
                    if (!win) return;
                    win.focus();
                    win.print();
                  }}
                  disabled={!ready}
                  className="inline-flex items-center gap-1.5 rounded-md border border-outline px-3 py-1.5 text-body-sm font-medium text-on-surface hover:bg-surface-variant disabled:opacity-50"
                  title="Print this document with letterhead"
                >
                  <Printer className="h-3.5 w-3.5" /> Print
                </button>
                <button
                  type="button"
                  onClick={() => download.mutate()}
                  disabled={!ready || download.isPending}
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-body-sm font-medium text-on-primary hover:bg-primary/90 disabled:opacity-60"
                >
                  {download.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                  {download.isPending ? 'Generating…' : 'Download PDF'}
                </button>
              </div>
            </div>

            <div className="relative flex-1 overflow-auto p-6">
              {(letterLoading || !ready) && (
                <div className="absolute inset-0 flex items-center justify-center"><Spinner /></div>
              )}
              {html && (
                <div className="mx-auto max-w-[880px] overflow-hidden rounded-md border border-outline bg-surface shadow-surface-2">
                  <iframe
                    key={`${docCode}-${version}`}
                    ref={iframeRef}
                    srcDoc={html}
                    title={activeDoc.label}
                    onLoad={handleIframeLoad}
                    className="h-[1150px] w-full border-0"
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Divider() {
  return <div className="mx-1 h-5 w-px shrink-0 bg-outline" />;
}
